import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { roadmapMarketplaceService, RoadmapTemplate, RoadmapRating, TenantRoadmap } from '@/services/roadmapMarketplace';
import { RoadmapProject } from '@/services/roadmapMarketplace';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { ExcelDownloadUpload } from '@/components/roadmap-marketplace/ExcelDownloadUpload';
import { VisualRoadmapTimeline } from '@/components/roadmap/VisualRoadmapTimeline';
import templateService from '@/services/templateService';
import { extractProjectsFromHierarchy } from '@/utils/roadmapProjectExtractor';
import { PencilIcon } from '@heroicons/react/24/outline';
import { PageContextWrapper, getContextButtonClasses } from '@/components/roadmap/PageContextWrapper';

// =====================================================
// ROADMAP DETAIL PAGE
// TMOS Platform - Core Feature Page
// =====================================================

export const RoadmapDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [isTenantRoadmap, setIsTenantRoadmap] = useState(false);
  
  // Determine context based on route for visual differentiation
  const isTenantRoute = location.pathname.includes('/tenant/roadmaps/');
  const pageContext = isTenantRoute ? 'tenant-roadmap-builder' : 'marketplace';
  
  const [roadmap, setRoadmap] = useState<(RoadmapTemplate & { rating_stats: RoadmapRating }) | TenantRoadmap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean>(true); // Default to true, will be checked for private templates
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastAccessRequest, setLastAccessRequest] = useState<{ created_at: string } | null>(null);
  

  // =====================================================
  // OWNERSHIP CHECK
  // =====================================================

  const isRoadmapOwner = (): boolean => {
    if (!currentUser || !roadmap) {
            return false;
    }
    
        
    // For tenant roadmaps, check if the current user's tenant_id matches the roadmap's tenant_id
    if (isTenantRoadmap && 'tenant_id' in roadmap) {
      const isOwner = currentUser.tenantId === roadmap.tenant_id;
            return isOwner;
    }
    
    // For template roadmaps, check if the current user's ID matches the template's template_owner_id
    if (!isTenantRoadmap && 'template_owner_id' in roadmap) {
      const isOwner = currentUser.id === roadmap.template_owner_id;
            return isOwner;
    }
    
        return false;
  };

  // =====================================================
  // DATA LOADING
  // =====================================================

  useEffect(() => {
    if (id) {
      loadRoadmap();
    }
  }, [id]);

  const extractProjectsFromHierarchyLocal = (roadmapData: any) => {
    // Use shared extraction utility
    const extractedProjects = extractProjectsFromHierarchy(roadmapData);
    setProjects(extractedProjects);
  };

  const loadRoadmap = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Determine roadmap type from URL path
      const isTenantFromUrl = window.location.pathname.includes('/tenant/');
      let roadmapData;
      
      if (isTenantFromUrl) {
        // Load tenant roadmap via canonical editor-data endpoint (same source as builder timeline).
        const editorData = await roadmapMarketplaceService.getTenantRoadmapEditorData(id);
        roadmapData = {
          ...editorData.roadmap,
          hierarchical_content: editorData.hierarchical_content
        };
        setIsTenantRoadmap(true);
        setHasAccess(true); // Tenant roadmaps are always accessible to the tenant
      } else {
        // Load as template roadmap
        roadmapData = await roadmapMarketplaceService.getRoadmapTemplate(id);
        setIsTenantRoadmap(false);
        
        // Check access for private templates
        if (roadmapData.marketplace_visibility === 'PRIVATE') {
          // Check if user is the owner
          const isOwner = currentUser?.id === roadmapData.template_owner_id;
          
          if (isOwner) {
            setHasAccess(true); // Owners always have access
          } else {
            // Check if user has been granted access
            try {
              const accessGranted = await templateService.checkAccess(id);
              setHasAccess(accessGranted);
              
              // If no access, check for last access request
              if (!accessGranted && currentUser?.id) {
                const lastRequest = await templateService.getLastAccessRequest(id);
                setLastAccessRequest(lastRequest);
              }
            } catch (err) {
              console.error('Error checking access:', err);
              setHasAccess(false); // Default to no access on error
            }
          }
        } else {
          // PUBLIC or DRAFT templates - check access based on visibility
          setHasAccess(roadmapData.marketplace_visibility === 'PUBLIC');
        }
      }
      
            
      setRoadmap(roadmapData);
      
            
      // Use one canonical timeline path for tenant + template: extract from hierarchical content.
      if (roadmapData && 'hierarchical_content' in roadmapData && roadmapData.hierarchical_content) {
        extractProjectsFromHierarchyLocal(roadmapData);
      } else {
        // Fallback for legacy/template flows without hierarchical_content
        await loadProjects(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('roadmapDetail.failedToLoadRoadmapDetails'));
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async (isTenant: boolean = isTenantRoadmap) => {
    if (!id) return;
    
    try {
      if (isTenant) {
        // For tenant roadmaps, get projects from the tenant roadmap
        const tenantProjects = await roadmapMarketplaceService.getRoadmapProjects(id);
        setProjects(tenantProjects);
      } else {
        // For template roadmaps, extract projects from hierarchical content
        const templateProjects = await roadmapMarketplaceService.getRoadmapProjectTemplates(id);
        setProjects(templateProjects);
      }
    } catch (err: any) {
      console.error('Error loading projects:', err);
      // Extract projects from hierarchical_content as fallback
      if (roadmap && 'hierarchical_content' in roadmap) {
        extractProjectsFromHierarchyLocal(roadmap);
      }
    }
  };

  // =====================================================
  // EVENT HANDLERS
  // =====================================================

  const handleDownload = async () => {
    if (!roadmap) return;
    
    // Don't allow download if user doesn't have access
    if (!hasAccess && (roadmap as RoadmapTemplate).marketplace_visibility === 'PRIVATE') {
      setError('You do not have access to download this private template. Please request access first.');
      return;
    }
    
    try {
      setDownloading(true);
      await roadmapMarketplaceService.downloadRoadmap(roadmap.id);
      navigate('/tenant/roadmaps');
    } catch (err: any) {
      setError(err.response?.data?.message || t('roadmapDetail.failedToDownloadRoadmap'));
    } finally {
      setDownloading(false);
    }
  };

  const handleRequestAccess = async () => {
    if (!roadmap || !id) return;
    
    try {
      setRequestingAccess(true);
      setError(null);
      setSuccessMessage(null);
      await templateService.requestAccess(id);
      
      // Update last access request date
      const lastRequest = await templateService.getLastAccessRequest(id);
      setLastAccessRequest(lastRequest);
      
      // Show success message and check access again
      setTimeout(async () => {
        const accessGranted = await templateService.checkAccess(id);
        setHasAccess(accessGranted);
        if (accessGranted) {
          // Access was granted immediately (owner approved or auto-approved)
          setSuccessMessage('Access granted! You can now download this template.');
          setError(null);
        } else {
          // Access request submitted, waiting for approval
          setSuccessMessage('Access request submitted. The template owner will be notified.');
          setError(null);
        }
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to request access. Please try again.');
      setSuccessMessage(null);
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleRateRoadmap = async () => {
    if (!roadmap || rating === 0) return;
    
    try {
      await roadmapMarketplaceService.rateRoadmap(roadmap.id, rating, comments);
      setShowRatingForm(false);
      setRating(0);
      setComments('');
      // Reload roadmap to get updated rating stats
      await loadRoadmap();
    } catch (err: any) {
      setError(err.response?.data?.message || t('roadmapDetail.failedToSubmitRating'));
    }
  };

  // Unused function - kept for future use
  // @ts-expect-error - Suppress unused variable warning
  const _handleTimelineUpdate = async (projectId: string, updates: { estimated_duration_months?: number; start_month?: number }) => {
    if (!roadmap) return;
    
    // OPTIMISTIC UPDATE: Update local state immediately
    const previousProjects = [...projects];
    setProjects(prevProjects => 
      prevProjects.map(p => 
        p.id === projectId 
          ? { ...p, ...updates }
          : p
      )
    );
    
    try {
      // Update project timeline via API (in background)
      if (isTenantRoadmap) {
        // For tenant roadmaps, update via tenant roadmap API
        // TODO: Implement tenant roadmap project timeline update endpoint
      } else {
        // For template roadmaps, update via template project timeline endpoint
        await roadmapMarketplaceService.updateProjectTimeline(roadmap.id, projectId, updates);
      }
      
      // Don't reload - the optimistic update already has the correct data
      // The backend is now in sync, so we're good
    } catch (err: any) {
      console.error('Error updating project timeline:', err);
      // ROLLBACK: Restore previous state if update fails
      setProjects(previousProjects);
      setError(err.response?.data?.message || t('roadmapDetail.failedToUpdateTimeline') || 'Failed to update project timeline');
    }
  };

  // =====================================================
  // RENDER HELPERS
  // =====================================================

  const renderComplexityStars = (level: number | undefined | null) => {
    // Validate and clamp level to valid range (0-5)
    // This prevents RangeError when level is negative, undefined, null, or > 5
    const validLevel = Math.max(0, Math.min(5, level || 0));
    const filledStars = Math.max(0, validLevel);
    const emptyStars = Math.max(0, 5 - validLevel);
    return '★'.repeat(filledStars) + '☆'.repeat(emptyStars);
  };

  const renderCostRange = (min?: number, max?: number) => {
    if (!min && !max) return t('roadmapMarketplace.notSpecified');
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    if (min) return `${t('roadmapMarketplace.from')} $${min.toLocaleString()}`;
    if (max) return `${t('roadmapMarketplace.upTo')} $${max.toLocaleString()}`;
  };

  const renderTags = (tags: string[]) => {
    if (!tags || tags.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="px-3 py-1 text-sm bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] border border-[var(--color-border)] rounded-full"
          >
            #{tag}
          </span>
        ))}
      </div>
    );
  };

  const renderRatingStars = (rating: number, interactive = false, onChange?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive && onChange ? () => onChange(star) : undefined}
            className={`text-2xl ${interactive ? 'cursor-pointer' : ''} ${
              star <= rating ? 'text-[var(--color-warning)]' : 'text-[var(--color-border)]'
            }`}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const renderRatingDistribution = (distribution: Record<number, number>, total: number) => {
    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = distribution[star] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-2">
              <span className="text-sm text-[var(--color-primary-text)] w-8">{star}★</span>
              <div className="flex-1 bg-[var(--color-border)] rounded-full h-2">
                <div
                  className="bg-[var(--color-warning)] h-2 rounded-full"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-[var(--color-primary-text)] w-12">{count}</span>
            </div>
          );
        })}
      </div>
    );
  };





  // =====================================================
  // RENDER
  // =====================================================

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-[var(--color-surface-text)]">{t('roadmapDetail.loadingRoadmapDetails')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 text-[var(--color-error)] px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!roadmap) {
    return (
      <div className="text-center py-8">
        <div className="text-[var(--color-surface-text)]">{t('roadmapDetail.roadmapNotFound')}</div>
      </div>
    );
  }

  return (
    <PageContextWrapper context={pageContext}>
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-6">
            {/* Success Message */}
            {successMessage && (
              <div className="bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 text-[var(--color-success)] px-4 py-3 rounded">
                {successMessage}
              </div>
            )}
            {/* Error Message */}
            {error && (
              <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 text-[var(--color-error)] px-4 py-3 rounded">
                {error}
              </div>
            )}
          {/* Header */}
          <div className="flex justify-between items-start">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[var(--color-secondary-text)] mb-2">
            {roadmap.name}
          </h1>
          
          <p className="text-lg text-[var(--color-secondary-text)] mb-4">
            {roadmap.description}
          </p>
        </div>
        <div className="flex gap-3">
          {/* Edit Button - Only show for tenant roadmaps */}
          {isTenantRoadmap && roadmap && (
            <button
              onClick={() => navigate(`/tenant/roadmaps/builder?roadmap=${roadmap.id}`)}
              className={`px-4 py-2 ${getContextButtonClasses('tenant-roadmap-builder')} rounded-md transition-colors flex items-center gap-2`}
              title={t('roadmapDetail.editMyRoadmap') || 'Edit My Roadmap'}
            >
              <PencilIcon className="w-4 h-4" />
              {t('roadmapDetail.edit') || 'Edit'}
            </button>
          )}
          {/* Save Button or Request Access Button - Only show for templates */}
          {!isTenantRoadmap && roadmap && (
            <>
              {(roadmap as RoadmapTemplate).marketplace_visibility === 'PRIVATE' && !hasAccess ? (
                <div className="flex flex-col items-end">
                  <button
                    onClick={handleRequestAccess}
                    disabled={requestingAccess}
                    className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {requestingAccess ? t('roadmapDetail.requesting') : t('roadmapDetail.requestAccess')}
                  </button>
                  {lastAccessRequest && (
                    <span className="text-xs text-[var(--color-secondary-text)] mt-1">
                      {t('roadmapDetail.lastAccessRequestSent')}: {new Date(lastAccessRequest.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleDownload}
                  disabled={downloading || ((roadmap as RoadmapTemplate).marketplace_visibility === 'PRIVATE' && !hasAccess)}
                  className={`px-6 py-2 ${getContextButtonClasses(pageContext)} rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {downloading ? t('roadmapDetail.saving') : t('roadmapDetail.saveToMyRoadmaps')}
                </button>
              )}
            </>
          )}
          <button
            onClick={() => navigate(isTenantRoadmap ? '/tenant/roadmaps' : '/marketplace')}
            className="px-4 py-2 text-[var(--color-secondary-text)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-secondary-bg)] transition-colors"
          >
            {isTenantRoadmap ? t('roadmapDetail.backToMyRoadmaps') : t('roadmapDetail.backToMarketplace')}
          </button>
        </div>
      </div>

      {/* Key Information and Excel Actions */}
      <div className="grid grid-cols-1 gap-6">
        {/* Key Information */}
        <div className="bg-[var(--color-surface-bg)] p-6 rounded-lg border border-[var(--color-border)]">
          <div className="space-y-6">
            {/* Vision and Objectives Section */}
            {'hierarchical_content' in roadmap && (roadmap.hierarchical_content as any)?.hierarchy?.visions && (roadmap.hierarchical_content as any).hierarchy.visions.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[var(--color-surface-text)] border-b border-[var(--color-border)] pb-2">Vision & Objectives</h3>
                {(roadmap.hierarchical_content as any).hierarchy.visions.map((vision: any, visionIndex: number) => (
                  <div key={visionIndex} className="bg-[var(--color-secondary-bg)] p-4 rounded-lg border border-[var(--color-border)]">
                    <div className="mb-3">
                      <h4 className="text-md font-semibold text-[var(--color-secondary-text)] mb-2">{vision.title}</h4>
                      <p className="text-sm text-[var(--color-secondary-text)]">{vision.description}</p>
                    </div>
                    
                    {vision.objectives && vision.objectives.length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-[var(--color-secondary-text)]">Objectives:</h5>
                        {vision.objectives.map((objective: any, objIndex: number) => (
                          <div key={objIndex} className="ml-4 p-2 bg-[var(--color-surface-bg)] rounded border-l-2 border-[var(--color-primary-bg)]">
                            <div className="text-sm font-medium text-[var(--color-surface-text)]">{objective.title}</div>
                            <div className="text-xs text-[var(--color-surface-text)] mt-1">{objective.description}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Metadata Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Publisher Name - First field */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Publisher</label>
                <p className="text-[var(--color-surface-text)] font-medium">
                  {roadmap.tenant_name || t('roadmapDetail.unknownPublisher') || 'Unknown'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapMarketplace.industry')}</label>
                <p className="text-[var(--color-surface-text)]">{roadmap.industry}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapMarketplace.functionalArea')}</label>
                <p className="text-[var(--color-surface-text)]">{roadmap.functional_area}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapMarketplace.transformationType')}</label>
                <p className="text-[var(--color-surface-text)]">{roadmap.transformation_type}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapMarketplace.complexityLevel')}</label>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--color-surface-text)]">{roadmap.complexity_level}/5</span>
                  <span className="text-yellow-500">{renderComplexityStars(roadmap.complexity_level || 0)}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapDetail.duration')}</label>
                <p className="text-[var(--color-surface-text)]">{roadmap.estimated_duration_months} {t('roadmapMarketplace.months')}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapDetail.costRange')}</label>
                <p className="text-[var(--color-surface-text)]">
                  {renderCostRange(roadmap.estimated_cost_range_min, roadmap.estimated_cost_range_max)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapDetail.status')}</label>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  roadmap.status === 'PUBLISHED' 
                    ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' 
                    : roadmap.status === 'DRAFT'
                    ? 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]'
                    : 'bg-[var(--color-border)] text-[var(--color-secondary-text)]'
                }`}>
                  {roadmap.status}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapDetail.created')}</label>
                <p className="text-[var(--color-surface-text)]">
                  {new Date(roadmap.created_at).toLocaleDateString()}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapDetail.updated')}</label>
                <p className="text-[var(--color-surface-text)]">
                  {new Date(roadmap.updated_at).toLocaleDateString()}
                </p>
              </div>
              
              {roadmap.published_at && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('roadmapDetail.published')}</label>
                  <p className="text-[var(--color-surface-text)]">
                    {new Date(roadmap.published_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Excel Import/Export - Hidden but not deleted */}
        {isRoadmapOwner() && (
          <div className="lg:col-span-1 hidden">
            <ExcelDownloadUpload
              roadmapId={roadmap?.id || ''}
              isTenantRoadmap={isTenantRoadmap}
              onImportSuccess={() => {
                // Reload roadmap data after successful import
                loadRoadmap();
              }}
            />
          </div>
        )}
      </div>

      {/* Visual Timeline (Read-only for marketplace) */}
      {projects.length > 0 && (
        <div className="w-full">
          <div className="bg-[var(--color-surface-bg)] p-6 rounded-lg border border-[var(--color-border)]">
            <h2 className="text-xl font-semibold text-[var(--color-surface-text)] mb-4">
              {t('roadmapDetail.visualTimeline') || 'Visual Timeline'}
            </h2>
            <VisualRoadmapTimeline
              projects={projects}
              roadmapName={roadmap.name}
              totalDuration={Math.max(36, ...projects.map(p => (p.start_month || 0) + p.estimated_duration_months))}
              isEditable={false}
            />
          </div>
        </div>
      )}


      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">


          {/* Tags */}
          {roadmap.tags && roadmap.tags.length > 0 && (
            <div className="bg-[var(--color-surface-bg)] p-6 rounded-lg border border-[var(--color-border)]">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)] mb-4">{t('roadmapDetail.tags')}</h2>
              {renderTags(roadmap.tags)}
            </div>
          )}

                     {/* Rating Section - Only show for templates */}
           {!isTenantRoadmap && (
             <div className="bg-[var(--color-primary-bg)] p-6 rounded-lg border border-[var(--color-border)]">
               <div className="flex justify-between items-center mb-4">
                 <h2 className="text-xl font-semibold text-[var(--color-primary-text)]">{t('roadmapDetail.communityRatings')}</h2>
                 <button
                   onClick={() => setShowRatingForm(!showRatingForm)}
                   className="px-4 py-2 bg-[var(--color-primary-text)] text-[var(--color-primary-bg)] rounded-md hover:bg-[var(--color-secondary-bg)] transition-colors"
                 >
                   {t('roadmapDetail.rateThisRoadmap')}
                 </button>
               </div>

            {/* Rating Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-primary-text)]">
                  {roadmap.rating_stats?.average_rating?.toFixed(1) || '0.0'}
                </div>
                <div className="text-sm text-[var(--color-primary-text)]">{t('roadmapDetail.averageRating')}</div>
                {renderRatingStars(Math.round(roadmap.rating_stats?.average_rating || 0))}
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-primary-text)]">
                  {roadmap.rating_stats?.total_ratings || 0}
                </div>
                <div className="text-sm text-[var(--color-primary-text)]">{t('roadmapDetail.totalRatings')}</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-[var(--color-primary-text)]">
                  {(roadmap.rating_stats?.total_ratings || 0) > 0 ? t('roadmapDetail.active') : t('roadmapDetail.new')}
                </div>
                <div className="text-sm text-[var(--color-primary-text)]">{t('roadmapDetail.status')}</div>
              </div>
            </div>

            {/* Rating Distribution */}
            <div>
              <h3 className="text-lg font-medium text-[var(--color-primary-text)] mb-3">{t('roadmapDetail.ratingDistribution')}</h3>
              {renderRatingDistribution(roadmap.rating_stats?.rating_distribution || {}, roadmap.rating_stats?.total_ratings || 0)}
            </div>

            {/* Rating Form */}
            {showRatingForm && (
              <div className="mt-6 p-4 bg-[var(--color-surface-bg)] rounded-lg">
                <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-3">{t('roadmapDetail.rateThisRoadmapTitle')}</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">{t('roadmapDetail.yourRating')}</label>
                    {renderRatingStars(rating, true, setRating)}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">{t('roadmapDetail.commentsOptional')}</label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
                      placeholder={t('roadmapDetail.commentsPlaceholder')}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleRateRoadmap}
                      disabled={rating === 0}
                      className="px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('roadmapDetail.submitRating')}
                    </button>
                    <button
                      onClick={() => setShowRatingForm(false)}
                      className="px-4 py-2 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-secondary-bg)] transition-colors"
                    >
                      {t('roadmapDetail.cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

                 {/* Right Column - Actions & Metadata */}
         <div className="space-y-6">
           {/* Additional content can go here if needed */}
        </div>
      </div>
        </div>
        </div>
      </div>
    </PageContextWrapper>
  );
};
